import { inject } from '@angular/core';

import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  type,
  withMethods,
  withState
} from '@ngrx/signals';
import {
  entityConfig,
  removeAllEntities,
  setAllEntities,
  updateEntity,
  withEntities
} from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { of, pipe, switchMap } from 'rxjs';

import {
  ErrorResponse,
  RxMethodCallback
} from 'src/app/shared/models/rxMethod.models';
import { SelectedEventDetailsStore } from 'src/app/shared/services/events/events.state';
import { GetHostFeedbackRequestPayload } from 'src/app/shared/services/events/feedback/event-feedback.models';
import { EventFeedbackService } from 'src/app/shared/services/events/feedback/event-feedback.service';
import {
  BaseFeedbackSlot,
  FeedbackSlot,
  HostFeedback
} from 'src/app/shared/services/public-events/feedback/public-events-feedback.models';

interface EventFeedbackState {
  getFeedbackSlotsInflight?: boolean;
  submitFeedbackInflight?: boolean;
  getHostFeedbackInflight?: boolean;
}

const defaultEventFeedbackState: EventFeedbackState = {
  getFeedbackSlotsInflight: undefined,
  submitFeedbackInflight: undefined,
  getHostFeedbackInflight: undefined
};

const guestFeedbackCollection = entityConfig({
  entity: type<FeedbackSlot>(),
  selectId: (entity) => entity.entityId,
  collection: 'event-guest-feedback'
});

const hostFeedbackCollection = entityConfig({
  entity: type<HostFeedback>(),
  selectId: (feedback) => feedback.hostOrg.orgId,
  collection: 'event-host-feedback'
});

export const EventFeedbackStore = signalStore(
  { providedIn: 'root' },
  withEntities(guestFeedbackCollection),
  withEntities(hostFeedbackCollection),
  withState<EventFeedbackState>(defaultEventFeedbackState),
  withMethods((store) => {
    const eventStore = inject(SelectedEventDetailsStore);
    const feedbackService = inject(EventFeedbackService);
    return {
      getFeedbackSlots: rxMethod<
        {
          eventId?: string;
          payload: { userId: string };
        } & RxMethodCallback<FeedbackSlot[]>
      >(
        pipe(
          switchMap(
            ({ eventId: explicitEventId, payload, onSuccess, onFail }) => {
              const eventId = explicitEventId ?? eventStore.details()?.eventId;
              if (!eventId) {
                onFail?.();
                return of(null);
              }

              patchState<EventFeedbackState>(store, {
                getFeedbackSlotsInflight: true
              });

              return feedbackService
                .getFeedbackSlots$(eventId, payload.userId)
                .pipe(
                  tapResponse({
                    next: (response) => {
                      patchState(
                        store,
                        setAllEntities(response, guestFeedbackCollection)
                      );
                      patchState<EventFeedbackState>(store, {
                        getFeedbackSlotsInflight: undefined
                      });

                      onSuccess?.(response);
                    },
                    error: (error: ErrorResponse) => {
                      patchState<EventFeedbackState>(store, {
                        getFeedbackSlotsInflight: undefined
                      });

                      onFail?.(error);
                    }
                  })
                );
            }
          )
        )
      ),
      submitFeedback: rxMethod<
        {
          eventId?: string;
          payload: BaseFeedbackSlot;
          userId: string;
        } & RxMethodCallback<BaseFeedbackSlot>
      >(
        pipe(
          switchMap(
            ({
              eventId: explicitEventId,
              payload,
              userId,
              onSuccess,
              onFail
            }) => {
              const eventId = explicitEventId ?? eventStore.details()?.eventId;
              if (!eventId) {
                onFail?.();
                return of(null);
              }

              patchState<EventFeedbackState>(store, {
                submitFeedbackInflight: true
              });

              return feedbackService
                .submitFeedback$(eventId, userId, payload)
                .pipe(
                  tapResponse({
                    next: (response) => {
                      const changes = {
                        isAnonymous: !!response.isAnonymous,
                        feedback: response.feedback || ''
                      };
                      patchState(
                        store,
                        updateEntity(
                          {
                            id: response.entityId,
                            changes
                          },
                          guestFeedbackCollection
                        )
                      );
                      patchState<EventFeedbackState>(store, {
                        submitFeedbackInflight: undefined
                      });

                      onSuccess?.(response);
                    },
                    error: (error: ErrorResponse) => {
                      patchState<EventFeedbackState>(store, {
                        submitFeedbackInflight: undefined
                      });

                      onFail?.(error);
                    }
                  })
                );
            }
          )
        )
      ),
      getHostFeedback: rxMethod<
        GetHostFeedbackRequestPayload & RxMethodCallback<HostFeedback[]>
      >(
        pipe(
          switchMap(
            ({ eventId: explicitEventId, payload, onSuccess, onFail }) => {
              const eventId = explicitEventId ?? eventStore.eventId();
              if (!eventId) {
                onFail?.();
                return of(null);
              }

              patchState<EventFeedbackState>(store, {
                getHostFeedbackInflight: true
              });

              return feedbackService.getHostFeedback$(eventId, payload).pipe(
                tapResponse({
                  next: (response) => {
                    patchState(
                      store,
                      setAllEntities(response, hostFeedbackCollection)
                    );
                    patchState<EventFeedbackState>(store, {
                      getHostFeedbackInflight: undefined
                    });

                    onSuccess?.(response);
                  },
                  error: (error: ErrorResponse) => {
                    patchState<EventFeedbackState>(store, {
                      getHostFeedbackInflight: undefined
                    });

                    onFail?.(error);
                  }
                })
              );
            }
          )
        )
      ),
      reset: () => {
        removeAllEntities(guestFeedbackCollection);
        removeAllEntities(hostFeedbackCollection);
        patchState<EventFeedbackState>(store, defaultEventFeedbackState);
      }
    };
  })
);
